import React, { useState } from "react";
import { api, fileUrl } from "@/planner/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus, CheckCircle2 } from "lucide-react";

// Search Sandstar's cloud product library (GET /sandstar/cloud). The Ark machines only recognize items in
// Dan's own Sandstar library, so each result says whether it's there already. Adding it to the Sandstar
// library is done in Sandstar Ops; "Add to catalog" only puts it in our catalog for planograms.
export default function SandstarCloudDialog({ open, onOpenChange, canEdit, onAdded }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(null);

  const search = async (page = 1) => {
    if (q.trim().length < 2) { toast.error("Type at least 2 letters."); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/sandstar/cloud", { params: { q: q.trim(), page } });
      setRes(page > 1 && res ? { ...data, items: [...res.items, ...data.items] } : data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't search Sandstar"); }
    finally { setBusy(false); }
  };
  const add = async (x) => {
    setAdding(x.upc);
    try {
      const { data } = await api.post("/sandstar/cloud/add", x);
      toast.success(data.created ? `Added ${x.name} to your catalog` : "Already in your catalog");
      setRes((r) => ({ ...r, items: r.items.map((i) => (i.upc === x.upc ? { ...i, product_id: data.product.id } : i)) }));
      onAdded && onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add it"); }
    finally { setAdding(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="sandstar-cloud-dialog">
        <DialogHeader><DialogTitle>Sandstar cloud library</DialogTitle></DialogHeader>
        <p className="-mt-1 text-xs text-muted-foreground">
          Your machines only recognize items in your own Sandstar library. Items marked "Not in your library" need adding in Sandstar Ops (Product library) before you stock them.
        </p>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); search(1); }}>
          <Input data-testid="sandstar-cloud-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Sour Patch, Klondike, Red Bull" autoFocus />
          <Button type="submit" disabled={busy}><Search className="mr-2 h-4 w-4" />{busy ? "Searching…" : "Search"}</Button>
        </form>
        {res && (
          res.items.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Nothing found in the cloud library.</p> : (
            <div className="max-h-[55vh] space-y-1.5 overflow-y-auto pr-1">
              {res.items.map((x) => (
                <div key={x.upc + x.name} className="flex items-center gap-3 rounded-lg border p-2" data-testid="sandstar-cloud-row">
                  {x.image_path ? <img src={fileUrl(x.image_path)} alt="" className="h-12 w-12 shrink-0 rounded object-contain" /> : <div className="h-12 w-12 shrink-0 rounded bg-muted" />}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{x.name}{x.size && <span className="font-normal text-muted-foreground"> · {x.size}</span>}</div>
                    <div className="text-xs text-muted-foreground">{x.brand && `${x.brand} · `}{x.upc || "no barcode"}</div>
                    <div className="mt-0.5 text-xs">
                      {x.in_library
                        ? <span className="inline-flex items-center gap-1 font-semibold text-green-700"><CheckCircle2 className="h-3 w-3" />In your Sandstar library</span>
                        : <span className="font-semibold text-amber-700">Not in your library: add it in Sandstar Ops first</span>}
                    </div>
                  </div>
                  {x.product_id
                    ? <span className="shrink-0 text-xs text-muted-foreground">In catalog</span>
                    : canEdit && x.upc && <Button size="sm" variant="outline" disabled={adding === x.upc} onClick={() => add(x)} data-testid="sandstar-cloud-add"><Plus className="mr-1 h-3.5 w-3.5" />Add to catalog</Button>}
                </div>
              ))}
              {res.has_more && <Button variant="ghost" className="w-full" disabled={busy} onClick={() => search(res.page + 1)}>{busy ? "Loading…" : "More results"}</Button>}
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
